import type { EditTypeItem } from '@rialtic/types'
import type { Frontiers, LoroDoc } from 'loro-crdt'
import type { AnswerValue } from './contract'
import type { ClaimJudgment, ClaimSetSummary, UnreachableMark } from './claims'
import type { PolicyDecision } from './decisions'
import type { PolicyVersion } from './versions'

/* Shared vocabulary for one policy's room (`PolicyAgent`) and the registry
   (`PolicyManagerAgent`). Pure: no DO, no network — the admin app imports it
   through `@worker/policy-builder/client`, so the browser and the Durable
   Object read the document and decide what is editable with the same code. */

export const POLICY_DOC_SCHEMA_VERSION = 1
export const POLICY_MANAGER_ROOM = 'default'

/** What the registry socket sends when a policy's row or mentions change:
 *  a nudge to refetch, never the data, so reads stay paged and filtered. */
export const POLICY_REGISTRY_CHANGED = 'policy-registry-changed'
export interface PolicyRegistryChanged {
  type: typeof POLICY_REGISTRY_CHANGED
  policyId: string
}

export const POLICY_STAGES = [
  'scoping',
  'design',
  'validation',
  'published',
  'dismissed',
] as const
export type PolicyStage = (typeof POLICY_STAGES)[number]

export const POLICY_STAGE_LABELS: Record<PolicyStage, string> = {
  scoping: 'Scoping',
  design: 'In design',
  validation: 'In validation',
  published: 'Done',
  dismissed: 'Dismissed',
}

export const POLICY_ROLES = ['scoping', 'design', 'validation'] as const
export type PolicyRole = (typeof POLICY_ROLES)[number]

/**
 * Loro containers in one policy document.
 *
 * There is deliberately no activity container: the audit trail lives only in
 * DO SQL and is read through the `activity` command, so reconciling after a
 * frame never has to diff the whole history.
 */
export const POLICY_DOC = {
  meta: 'meta',
  sources: 'sources',
  answers: 'answers',
  citations: 'citations',
  notAsked: 'notAsked',
  /** Field id → who pulled a dormant row onto the worksheet, and when. */
  pulledIn: 'pulledIn',
  questions: 'questions',
  /** Server mirror of `policy_suggestion`. */
  suggestions: 'suggestions',
  /** Server mirror of per-row `policy_field_version` counts. */
  fieldHistory: 'fieldHistory',
  /** Server-written at `seed()`: fieldId → the machine's proposed value, never edited after. */
  proposals: 'proposals',
  /** Server mirror of each field's current `FieldResolution`, derived from `proposals` and `policy_field_version`. */
  fieldResolutions: 'fieldResolutions',
  /** Server mirror of `policy_note`: per-row notes anyone may add in any stage. */
  notes: 'notes',
  /** agent-kit `Comments` list — its default for the `description` text container, so the client reads the same list unconfigured. */
  comments: 'description:comments',
  nodes: 'nodes',
  insights: 'insights',
  metadata: 'metadata',
  datasets: 'datasets',
  externalDocs: 'externalDocs',
  designMeta: 'designMeta',
  /** Code lists, bound onto tree nodes and synced to Nucleus as dataset bindings. */
  codeLists: 'codeLists',
  /** Node id → `NodeLinks`: the tree's traceability to the contract. */
  nodeLinks: 'nodeLinks',
  /** `<direction>:<node id>` → where someone dragged that node in that tree layout. */
  layout: 'layout',
  description: 'description',
  /** Server mirror of `policy_decision`: every approve/challenge/respond/publish/reopen, immutable. */
  decisions: 'decisions',
  /** Server mirror of `policy_version`: each distinct definition the policy has ever had. */
  versions: 'versions',
  /** Server mirror of the one stored `policy_claim_set` row, or empty before the first generation. */
  claims: 'claims',
  /** Case name → the reviewer's judgment of that claim. */
  judgments: 'judgments',
  /** Path key → a reviewer's note that the path cannot occur. */
  unreachable: 'unreachable',
  /** Where the tree stands against its Nucleus JDM. */
  jdm: 'jdm',
} as const

// --- design artefacts, as the builder names them -----------------------------

export const INSIGHT_TYPES = [
  'Not Applicable',
  'Claim Line Valid',
  'Claim Line Not Payable',
  'Claim Line Partially Payable',
] as const
export type PolicyInsightType = (typeof INSIGHT_TYPES)[number]

/** Only an outcome that withholds payment carries a CARC (plus a RARC where
 *  one applies), and it must (D-N1); Valid and Not applicable carry none. */
export const needsCarc = (type: PolicyInsightType | undefined): boolean =>
  type === 'Claim Line Not Payable' || type === 'Claim Line Partially Payable'

// --- fixed settings forms (D-N14) ---------------------------------------------

/** How settled a setting is on the engines team's settings sheet. */
export const SETTING_STATUSES = {
  decided: 'Decided in JDM',
  candidate: 'Candidate',
  proposed: 'Proposed',
  open: 'Open question',
} as const
export type SettingStatus = keyof typeof SETTING_STATUSES

export interface SettingField {
  id: string
  label: string
  /** The sheet's status, until a designer records another for this entry. */
  status: SettingStatus
  hint: string
  /** A pick list; free text when absent. */
  options?: readonly string[]
  /** Fields the engine reads as one setting share this heading. */
  group?: string
}

/* ponytail: every value is a string (free text or one pick). D2's ordered
   steps, D4's number + unit and O3's position + code are typed as text until
   the engine reads them; give those fields a structured `value` then. */
export const SETTING_FIELDS = {
  decision: [
    {
      id: 'D1',
      label: 'Lines checked',
      status: 'decided',
      hint: 'Proposed default: this claim, then other claims.',
      options: [
        'This claim',
        'Other claims',
        'This claim, then other claims',
        'Other claims as whole claims',
      ],
    },
    {
      id: 'D2',
      label: 'Filter steps',
      status: 'candidate',
      hint: 'The checks a prior line must pass, in order: field test, provider match, lookback window, required fields.',
    },
    {
      id: 'D3',
      label: 'Provider match',
      status: 'open',
      hint: 'Other methods: design to list them in the note.',
      options: [
        'Any provider',
        'Same provider by NPI + taxonomy',
        'Humana provider tree',
        'Other method',
      ],
    },
    {
      id: 'D4',
      label: 'Lookback window',
      status: 'decided',
      group: 'Lookback',
      hint: 'Lifetime, calendar year, fiscal year, calendar month, calendar week, or rolling days, weeks, months, years.',
    },
    {
      id: 'D5',
      label: 'Window ends on',
      status: 'decided',
      group: 'Lookback',
      hint: 'Default: end of the period.',
    },
    {
      id: 'D6',
      label: 'Ends that count',
      status: 'decided',
      group: 'Lookback',
      hint: 'Whether a line dated exactly on the first or last day counts.',
      options: ['Both ends', 'First day only', 'Last day only', 'Neither end'],
    },
    {
      id: 'D7',
      label: 'Required fields',
      status: 'candidate',
      hint: 'Missing on the line under review: the policy returns Claim Line Missing Data and stops. A prior line missing it is skipped.',
    },
    {
      id: 'D8',
      label: 'Add up across lines',
      status: 'candidate',
      hint: 'How matching lines combine into one value.',
      options: [
        'Number of lines',
        'Total of a field',
        'Highest',
        'Lowest',
        'Distinct values',
        'First',
        'Last',
      ],
    },
    {
      id: 'D9',
      label: 'OCL-S prioritization at this node',
      status: 'proposed',
      hint: 'Only for policies that filter history before prioritizing. Marks where same-claim line order applies.',
      options: ['On', 'Off'],
    },
  ],
  outcome: [
    {
      id: 'O1',
      label: 'Cited lines',
      status: 'proposed',
      hint: 'Which prior lines show as influencing. Proposed default: lines that matched on a Yes path; none on a No path or a valid outcome.',
      options: ['Lines that matched', 'All lines checked', 'None'],
    },
    {
      id: 'O2',
      label: 'Units change',
      status: 'decided',
      hint: 'A number, or a named value: units left, max units allowed. Formula text is never sent.',
    },
    {
      id: 'O3',
      label: 'Modifier change',
      status: 'decided',
      hint: 'A position 1–4, then a 2-character code. A blank code removes the modifier.',
    },
    {
      id: 'O4',
      label: 'Procedure code change',
      status: 'decided',
      hint: "A specific code, or a named value from engineering's list.",
    },
    {
      id: 'O5',
      label: 'Insight text fill-ins',
      status: 'decided',
      hint: "Values filled into the insight text: this line's code or units, prior claim numbers.",
    },
  ],
  policy: [
    {
      id: 'P1',
      label: 'OCL-S prioritization',
      status: 'proposed',
      hint: 'The engine applies it first. Proposed default: on for units policies.',
      options: ['On', 'Off'],
    },
    {
      id: 'P2',
      label: 'Build like',
      status: 'open',
      hint: 'A named pattern or a policy ID for NURO to draft from. Left empty, NURO finds similar policies.',
    },
    {
      id: 'P3',
      label: 'Data set (DDE table)',
      status: 'candidate',
      hint: 'The data set the policy reads values from, and the claim fields that pick the row.',
    },
  ],
} as const satisfies Record<string, readonly SettingField[]>
export type SettingsScope = keyof typeof SETTING_FIELDS

/** One filled setting, with a note to engineering the engine never reads. */
export interface SettingEntry {
  value: string
  status: SettingStatus
  note: string
}

/** A form's filled settings; an unfilled field has no key. */
export type Settings<S extends SettingsScope> = Partial<
  Record<(typeof SETTING_FIELDS)[S][number]['id'], SettingEntry>
>

/** `settings` with one field's entry patched; an entry back at its blank
 *  defaults is dropped, so a key present always means a field filled. */
export const withSetting = (
  settings: Partial<Record<string, SettingEntry>>,
  field: SettingField,
  patch: Partial<SettingEntry>,
): Partial<Record<string, SettingEntry>> => {
  const entry: SettingEntry = {
    value: '',
    status: field.status,
    note: '',
    ...settings[field.id],
    ...patch,
  }
  const next = { ...settings, [field.id]: entry }
  const blank =
    !entry.value.trim() && !entry.note.trim() && entry.status === field.status
  if (blank) delete next[field.id]
  return next
}

export interface PolicyInsight {
  id: string
  label: string
  insightType?: PolicyInsightType
  insightText: string
  editType?: Pick<EditTypeItem, 'edit_type' | 'id'>
  reasonCodeLevel1?: string
  reasonCodeLevel2?: string
  paymentInsight?: string
  paymentCalculationDefinition?: string
  insightEligibleForChange?: boolean
  changeFields?: string
  changeFieldsNewValueCalculationDefinition?: string
  settings?: Settings<'outcome'>
}

export interface PredicateData {
  id: string
  label: string
  yesPath: string
  noPath: string
  predicate: string
  settings?: Settings<'decision'>
}

export interface PolicyMetadata {
  text: string
}

export interface PolicyDataset {
  name: string
  url: string
  /** Decision nodes that read this data set, as a code list's `nodeIds` (D6b). */
  nodeIds?: string[]
}

export interface ExternalDoc {
  text: string
  url: string
}

export interface DesignMeta {
  engineRepoName: string
  effectiveStartDate: string
  effectiveEndDate: string
  rationale: string
  settings?: Settings<'policy'>
}

export const EMPTY_DESIGN_META: DesignMeta = {
  engineRepoName: '',
  effectiveStartDate: '',
  effectiveEndDate: '',
  rationale: '',
}

/** A bound set of billing codes, attached to tree nodes and synced to Nucleus as a dataset binding. */
export interface CodeList {
  name: string
  system: 'CPT' | 'HCPCS' | 'ICD-10' | 'Other'
  codes: string[]
  nodeIds: string[]
}

// --- scope ---------------------------------------------------------------------

export interface PolicySource {
  /** PaAPI reference document id. */
  id: string
  role: 'primary' | 'supporting' | 'dataset'
  title: string
}

/** One bounding box in unscaled PDF page units. */
export interface PdfRect {
  x: number
  y: number
  width: number
  height: number
}

/** Where a cited passage physically sits, captured once in the viewer. */
export interface PdfAnchor {
  /** sha256 of the file the rects were measured on; a viewer draws them
   *  only over that file. */
  documentHash: string
  /** Zero-based page. PaAPI excerpts carry no page, so this is viewer-captured. */
  pageIndex: number
  rects: PdfRect[]
}

export type CitationKind = 'source' | 'decision' | 'inherited'

export interface Citation {
  id: string
  fieldId: string
  kind: CitationKind
  /** The quoted passage, the decision text, or the inherited rationale. */
  text: string
  /** PaAPI reference document the passage is quoted from; null when none
   *  resolved or the citation is not a source quote. */
  referenceDocumentId: string | null
  excerptId: string | null
  /** Free-text pointer; mirrors a PaAPI excerpt's `location_reference`, '' when unknown. */
  cite: string
  anchor: PdfAnchor | null
  by: string
  at: string
}

export interface NotAskedEntry {
  reason: string
}

/** Who pulled a dormant contract row onto the worksheet, and when. */
export interface PulledIn {
  by: string
  at: string
}

/** One question asked about a contract field or the whole policy (`POLICY_ITEM`), and its resolution. */
export interface PolicyQuestion {
  id: string
  /** A contract field id, or `POLICY_ITEM` for a question on the policy as a whole. */
  fieldId: string
  text: string
  askedBy: string
  askedAt: string
  /** Who the question is for; the design/scoping queues read this to decide whose work it blocks. */
  to: PolicyRole
  /** Tags on the question, e.g. `team:scoping` — carried into the mention index. */
  mentions: string[]
  /** The test-claim case this question was raised from judging, when any. */
  claimName?: string
  answer: {
    text: string
    by: string
    at: string
    /** The citation scoping pointed to as the answer's basis, when it gave one. */
    citationId?: string
    /** A written rationale, when the answer is a judgment call rather than a citation. */
    rationale?: string
  } | null
  closedAt: string | null
  closedBy: string | null
}

export type ContractSuggestionStatus = 'pending' | 'accepted' | 'declined'

/** A proposed answer for one contract row, and how it was resolved. */
export interface ContractSuggestion {
  id: string
  fieldId: string
  value: AnswerValue
  rationale: string
  /** NURO's confidence in the value, 0–1; null when it gave none. */
  confidence: number | null
  by: string
  at: string
  status: ContractSuggestionStatus
  resolvedBy: string | null
  resolvedAt: string | null
  /** Optional reason given when declining. */
  feedback: string | null
}

export type ContractVersionOrigin =
  | 'edit'
  | 'suggestion'
  | 'restore'
  | 'confirm'

/** Who proposed a field's value: NURO's extraction, the triage decision, a system default, or the linked Content Library policy. */
export type ProposalSource = 'neuro' | 'triage' | 'system' | 'content-library'

/** A machine-proposed answer for one field, written at `seed()` or prefilled from Content Library. */
export interface FieldProposal {
  value: AnswerValue
  by: ProposalSource
  at: string
}

/**
 * How a field's current answer relates to its machine proposal: `proposed`
 * until the analyst acts on it, `accepted` once confirmed as-is, `redirected`
 * once edited to something else, `self-defined` when the analyst answered a
 * field with no proposal at all.
 */
export type FieldResolution =
  | 'proposed'
  | 'accepted'
  | 'redirected'
  | 'self-defined'

/** One recorded change to a row's answer. `null` means the answer was empty. */
export interface ContractVersion {
  id: string
  fieldId: string
  value: AnswerValue | null
  previous: AnswerValue | null
  by: string
  at: string
  origin: ContractVersionOrigin
  /** Rolled back by undo; still redoable until a new change lands. */
  undone: boolean
  /** How this version resolved a machine proposal, or `null` when none applied. */
  resolution: FieldResolution | null
  /** The proposal value being resolved, or `null` when none applied. */
  proposedValue: AnswerValue | null
}

export interface FieldHistoryState {
  versions: number
  canUndo: boolean
  canRedo: boolean
}

/** A point the design tree can be opened at or restored to, as Figma's version history lists them. */
export interface TreeVersion {
  /** `named:<id>` for one someone saved, `edit:<seq>` for an editing session. */
  id: string
  /** When it was saved, or the session's last edit. */
  at: string
  /** Who saved it, or everyone who edited the tree in the session, latest first. */
  authors: string[]
  /** A saved version's title; `null` for an editing session. */
  name: string | null
  description: string | null
  /** Where to open it: `doc.forkAt(frontiers)`. */
  frontiers: Frontiers
}

export const NO_FIELD_HISTORY: FieldHistoryState = {
  versions: 0,
  canUndo: false,
  canRedo: false,
}

/**
 * A note on the policy, a contract row or a tree node. Unlike a `decision` citation it is not evidence
 * and is not frozen with the scope: design and scoping can both add one in any
 * stage, and only its author may change or remove it.
 */
export interface PolicyNote {
  id: string
  fieldId: string
  body: string
  by: string
  at: string
  editedAt: string | null
}

/** A contract row's comment target in agent-kit `Comments`. */
export const fieldCommentTarget = (fieldId: string): string =>
  `field:${fieldId}`

export const fieldIdFromCommentTarget = (
  target: string | null,
): string | null =>
  target?.startsWith('field:') ? target.slice('field:'.length) : null

/** The policy itself as an item: comments here belong to no row. */
export const POLICY_ITEM = 'policy'

/**
 * A decision-tree node (predicate or insight) as an item: discussed, noted
 * and linked the way a contract row is, under this id in place of a field id.
 */
export const nodeItemId = (nodeId: string): string => `node:${nodeId}`

export const nodeIdFromItem = (itemId: string): string | null =>
  itemId.startsWith('node:') ? itemId.slice('node:'.length) : null

/**
 * An activity entry as an item: a thread can start on something that
 * happened to the policy. Discussed only — no notes, no links.
 */
export const activityItemId = (activityId: string): string =>
  `activity:${activityId}`

export const activityIdFromItem = (itemId: string): string | null =>
  itemId.startsWith('activity:') ? itemId.slice('activity:'.length) : null

/** What one tree node is traced to: the contract rows it implements. */
export interface NodeLinks {
  fields: string[]
}

/** A tree node's place on the canvas, in flow coordinates. */
export interface NodePosition {
  x: number
  y: number
}

const stringsOf = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : []

const readNodeLinks = (
  raw: Record<string, unknown>,
): Record<string, NodeLinks> =>
  Object.fromEntries(
    Object.entries(raw).map(([nodeId, links]) => {
      const entry = (links ?? {}) as Record<string, unknown>
      return [nodeId, { fields: stringsOf(entry.fields) }]
    }),
  )

export const readQuestion = (raw: unknown): PolicyQuestion => {
  const entry = (raw ?? {}) as Record<string, unknown>
  return {
    ...(entry as unknown as PolicyQuestion),
    to:
      entry.to === 'scoping' ||
      entry.to === 'design' ||
      entry.to === 'validation'
        ? entry.to
        : 'scoping',
    mentions: stringsOf(entry.mentions).length
      ? stringsOf(entry.mentions)
      : ['team:scoping'],
  }
}

const readPulledIn = (raw: Record<string, unknown>): Record<string, PulledIn> =>
  Object.fromEntries(
    Object.entries(raw).map(([fieldId, entry]) => {
      const value = (entry ?? {}) as Record<string, unknown>
      return [
        fieldId,
        {
          by: typeof value.by === 'string' ? value.by : '',
          at: typeof value.at === 'string' ? value.at : '',
        },
      ]
    }),
  )

const readCodeLists = (
  raw: Record<string, unknown>,
): Record<string, CodeList> =>
  Object.fromEntries(
    Object.entries(raw).map(([id, entry]) => {
      const value = (entry ?? {}) as Record<string, unknown>
      const system =
        value.system === 'CPT' ||
        value.system === 'HCPCS' ||
        value.system === 'ICD-10'
          ? value.system
          : 'Other'
      return [
        id,
        {
          name: typeof value.name === 'string' ? value.name : '',
          system,
          codes: stringsOf(value.codes),
          nodeIds: stringsOf(value.nodeIds),
        },
      ]
    }),
  )

// --- JDM sync ---------------------------------------------------------------

export type JdmSyncStatus = 'idle' | 'synced' | 'pending' | 'blocked' | 'failed'

/** Where the tree stands against its Nucleus JDM; server-owned, mirrored for reading. */
export interface PolicyJdm {
  /** Card version the tree last matched; null before the first sync. */
  version: string | null
  status: JdmSyncStatus
  /** Nucleus's refusal, when `status` is 'failed'. */
  error: string | null
}

export const EMPTY_JDM: PolicyJdm = {
  version: null,
  status: 'idle',
  error: null,
}

// --- workflow ---------------------------------------------------------------------

export interface PolicyMeta {
  schemaVersion: number
  /** The Content Library (PaAPI) policy id; the room is named by it. */
  id: string
  title: string
  sourceId: string | null
  candidateId: string | null
  initiativeId: string | null
  stage: PolicyStage
  stageChangedAt: string
  stageChangedBy: string
  owners: Record<PolicyRole, string | null>
  sourceChanged: boolean
  publishedAt: string | null
  createdAt: string
  /** Set when the policy was adopted from the Content Library, so its first
   *  Studio cycle is an update, not a new policy (V1). */
  adopted?: boolean
}

export const NO_OWNERS: Record<PolicyRole, string | null> = {
  scoping: null,
  design: null,
  validation: null,
}

export interface PolicyActivity {
  id: string
  at: string
  actor: string
  text: string
}

/** Plain-object read of one policy document. */
export interface PolicyDocView {
  meta: PolicyMeta
  sources: PolicySource[]
  answers: Record<string, AnswerValue>
  citations: Citation[]
  notAsked: Record<string, NotAskedEntry>
  pulledIn: Record<string, PulledIn>
  questions: PolicyQuestion[]
  suggestions: ContractSuggestion[]
  fieldHistory: Record<string, FieldHistoryState>
  /** The machine's proposed value per field, written once at `seed()`. */
  proposals: Record<string, FieldProposal>
  /** Each field's current `FieldResolution`, server-mirrored like `fieldHistory`. */
  fieldResolutions: Record<string, FieldResolution>
  notes: PolicyNote[]
  nodes: PredicateData[]
  insights: PolicyInsight[]
  metadata: PolicyMetadata[]
  datasets: PolicyDataset[]
  externalDocs: ExternalDoc[]
  designMeta: DesignMeta
  codeLists: Record<string, CodeList>
  nodeLinks: Record<string, NodeLinks>
  layout: Record<string, NodePosition>
  description: string
  /** Every approve/challenge/respond/publish/reopen, oldest first, immutable. */
  decisions: PolicyDecision[]
  /** Every distinct definition this policy has had, oldest first. */
  versions: PolicyVersion[]
  claims: ClaimSetSummary | null
  judgments: Record<string, ClaimJudgment>
  unreachable: Record<string, UnreachableMark>
  jdm: PolicyJdm
}

const EMPTY_META: PolicyMeta = {
  schemaVersion: POLICY_DOC_SCHEMA_VERSION,
  id: '',
  title: '',
  sourceId: null,
  candidateId: null,
  initiativeId: null,
  stage: 'scoping',
  stageChangedAt: '',
  stageChangedBy: '',
  owners: NO_OWNERS,
  sourceChanged: false,
  publishedAt: null,
  createdAt: '',
}

const isStoredStage = (value: unknown): value is PolicyStage | 'returned' =>
  typeof value === 'string' &&
  ([...POLICY_STAGES, 'returned'] as readonly string[]).includes(value)

/** A stored `'returned'` stage — from before the stage was deleted — reads as `'design'`. */
const readStage = (value: unknown): PolicyStage =>
  !isStoredStage(value) ? 'scoping' : value === 'returned' ? 'design' : value

const readMeta = (raw: Record<string, unknown>): PolicyMeta => ({
  ...EMPTY_META,
  ...raw,
  stage: readStage(raw.stage),
  owners: {
    ...NO_OWNERS,
    ...(raw.owners as Record<PolicyRole, string | null> | undefined),
  },
})

/**
 * Reads every container into plain data.
 *
 * Tolerates a document that has not been seeded, or was seeded by an older
 * `POLICY_DOC_SCHEMA_VERSION`: a missing container reads as empty rather than
 * throwing, so containers can be added without migrating stored rooms.
 */
export const readPolicyDoc = (doc: LoroDoc): PolicyDocView => {
  const claims = doc
    .getMap(POLICY_DOC.claims)
    .toJSON() as Partial<ClaimSetSummary>
  return {
    meta: readMeta(
      doc.getMap(POLICY_DOC.meta).toJSON() as Record<string, unknown>,
    ),
    sources: doc.getMovableList(POLICY_DOC.sources).toArray() as PolicySource[],
    answers: doc.getMap(POLICY_DOC.answers).toJSON() as Record<
      string,
      AnswerValue
    >,
    citations: doc.getMovableList(POLICY_DOC.citations).toArray() as Citation[],
    notAsked: doc.getMap(POLICY_DOC.notAsked).toJSON() as Record<
      string,
      NotAskedEntry
    >,
    pulledIn: readPulledIn(
      doc.getMap(POLICY_DOC.pulledIn).toJSON() as Record<string, unknown>,
    ),
    questions: (
      doc.getMovableList(POLICY_DOC.questions).toArray() as unknown[]
    ).map(readQuestion),
    suggestions: doc
      .getMovableList(POLICY_DOC.suggestions)
      .toArray() as ContractSuggestion[],
    fieldHistory: doc.getMap(POLICY_DOC.fieldHistory).toJSON() as Record<
      string,
      FieldHistoryState
    >,
    proposals: doc.getMap(POLICY_DOC.proposals).toJSON() as Record<
      string,
      FieldProposal
    >,
    fieldResolutions: doc
      .getMap(POLICY_DOC.fieldResolutions)
      .toJSON() as Record<string, FieldResolution>,
    notes: doc.getMovableList(POLICY_DOC.notes).toArray() as PolicyNote[],
    nodes: doc.getMovableList(POLICY_DOC.nodes).toArray() as PredicateData[],
    insights: doc
      .getMovableList(POLICY_DOC.insights)
      .toArray() as PolicyInsight[],
    metadata: doc
      .getMovableList(POLICY_DOC.metadata)
      .toArray() as PolicyMetadata[],
    datasets: doc
      .getMovableList(POLICY_DOC.datasets)
      .toArray() as PolicyDataset[],
    externalDocs: doc
      .getMovableList(POLICY_DOC.externalDocs)
      .toArray() as ExternalDoc[],
    designMeta: {
      ...EMPTY_DESIGN_META,
      ...(doc.getMap(POLICY_DOC.designMeta).toJSON() as Partial<DesignMeta>),
    },
    codeLists: readCodeLists(
      doc.getMap(POLICY_DOC.codeLists).toJSON() as Record<string, unknown>,
    ),
    nodeLinks: readNodeLinks(doc.getMap(POLICY_DOC.nodeLinks).toJSON()),
    layout: doc.getMap(POLICY_DOC.layout).toJSON() as Record<
      string,
      NodePosition
    >,
    description: doc.getText(POLICY_DOC.description).toString(),
    decisions: doc
      .getMovableList(POLICY_DOC.decisions)
      .toArray() as PolicyDecision[],
    versions: doc
      .getMovableList(POLICY_DOC.versions)
      .toArray() as PolicyVersion[],
    claims:
      typeof claims.graphSha === 'string' ? (claims as ClaimSetSummary) : null,
    judgments: doc.getMap(POLICY_DOC.judgments).toJSON() as Record<
      string,
      ClaimJudgment
    >,
    unreachable: doc.getMap(POLICY_DOC.unreachable).toJSON() as Record<
      string,
      UnreachableMark
    >,
    jdm: {
      ...EMPTY_JDM,
      ...(doc.getMap(POLICY_DOC.jdm).toJSON() as Partial<PolicyJdm>),
    },
  }
}
